// Pure eligibility predicate for a FINALLESS own-device rebuild (Path A). All I/O
// (storage, IndexedDB, the kill-switch) is resolved by the caller and passed in,
// so the load-bearing logic is unit-testable. Spec §14A/§14C.
//
// LOAD-BEARING: newestSegmentAge > STALE_REBUILD_MS (120s) is the authority, EXCEPT
// when the capture context reported its own teardown (`interrupted`) — a fact beats
// the inference the 120s window exists to make. The heartbeat is BLOCK-only
// (heartbeatFresh can stop a rebuild; its absence never authorizes one). orgMatch
// fails CLOSED (a missing/foreign orgId is not rebuilt).
export const STALE_REBUILD_MS = 120000; // ≥ 2× the ~60s background-tab throttle clamp
export const MAX_REBUILD_AGE_MS = 48 * 60 * 60 * 1000; // match the server sweep

// Per-segment budget for the RECOVERY upload timeout. A long recording's stitch +
// encode + multi-MB transmit on a slow connection needs more than the flat base
// timeout; scale by segment count so a 30-min (~180-segment) recovery gets ~6 min,
// not 3 — otherwise a legitimate recovery times out and is given up (silent loss).
export const RECOVERY_MS_PER_SEGMENT = 2000;
export function recoveryTimeoutMs(segmentCount, baseMs) {
  return Math.max(baseMs || 0, (segmentCount || 0) * RECOVERY_MS_PER_SEGMENT);
}

export function shouldRebuildFinalless({
  enabled,
  orgMatch,
  newestSegmentAge,
  oldestSegmentAge,
  heartbeatFresh,
  isActive,
  lockHeld,
  interrupted = false,
}) {
  if (!enabled) return false; // kill-switch
  if (!orgMatch) return false; // fails closed — never a cross-org upload
  if (isActive) return false; // this session is still recording it
  if (heartbeatFresh) return false; // another tab is recording it (block-only)
  if (!lockHeld) return false; // another tab won the recovery
  // The 120s window is how we GUESS that a finalless set is not still being
  // recorded. When the capture context told us it was being destroyed — the Chrome
  // extension's side panel closing mid-recording is the case this was measured on —
  // the guess is unnecessary and the wait is pure delay on audio that is already
  // dead. Every other guard still applies, so a marker left behind by a crashed tab
  // cannot rebuild a recording another tab is actively capturing.
  if (!interrupted && (typeof newestSegmentAge !== "number" || newestSegmentAge <= STALE_REBUILD_MS)) return false;
  if (typeof oldestSegmentAge === "number" && oldestSegmentAge > MAX_REBUILD_AGE_MS) return false; // beyond the sweep
  return true;
}

// How long until a finalless set that is too YOUNG to rebuild becomes eligible.
// Returns null when time is not what is holding it back (it is live, another tab
// owns it, or it is already old enough) — the caller only schedules a re-check when
// waiting is the whole reason it was skipped.
//
// Without this the drain never revisits the set: the skip is silent, no retry is
// armed, and the moment it qualifies passes unobserved. Measured in production on
// 2026-08-19 — a recording sat recoverable-but-unrecovered for seven minutes until
// an unrelated page load happened to run a pass.
export function msUntilRebuildEligible({ newestSegmentAge, heartbeatFresh, isActive, interrupted = false }) {
  if (isActive || heartbeatFresh) return null; // not time's fault
  if (interrupted) return 0; // already eligible
  if (typeof newestSegmentAge !== "number") return null;
  if (newestSegmentAge > STALE_REBUILD_MS) return 0;
  return STALE_REBUILD_MS - newestSegmentAge;
}
